// Admin Planning Tab — Milestones (jalons) + Tasks + Reports (comptes rendus)
// Timeline view, kanban + grouped tasks, markdown-light reports
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";

const CAT_LABELS_FR = {
  mecenat: "Mécénat", fresques: "Fresques", logistique: "Logistique",
  communication: "Communication", partenariats: "Partenariats", autre: "Autre",
};
const STATUS_LABELS_FR = {
  upcoming: "À venir", in_progress: "En cours", done: "Terminé", late: "En retard",
};
const TASK_STATUS_LABELS_FR = {
  todo: "À faire", in_progress: "En cours", done: "Terminé",
};
const TASK_STATUS_COLORS = {
  todo: { bg: "rgba(30,58,138,0.08)", fg: "var(--lapis)", border: "var(--lapis)" },
  in_progress: { bg: "rgba(212,175,55,0.15)", fg: "var(--gold)", border: "var(--gold)" },
  done: { bg: "rgba(83,128,72,0.12)", fg: "var(--moss)", border: "var(--moss)" },
};
const TASK_STATUSES = ["todo", "in_progress", "done"];
const STATUS_COLORS = {
  upcoming: { bg: "rgba(30,58,138,0.1)", fg: "var(--lapis)" },
  in_progress: { bg: "rgba(212,175,55,0.18)", fg: "var(--gold)" },
  done: { bg: "rgba(83,128,72,0.12)", fg: "var(--moss)" },
  late: { bg: "rgba(153,27,27,0.12)", fg: "var(--cinnabar)" },
};
const VIS_LABELS_FR = { private: "Privé (admin)", members: "Membres", public: "Public" };
const VIS_COLORS = {
  private: { bg: "rgba(0,0,0,0.06)", fg: "var(--ink-2)" },
  members: { bg: "rgba(30,58,138,0.1)", fg: "var(--lapis)" },
  public: { bg: "rgba(83,128,72,0.12)", fg: "var(--moss)" },
};

const emptyMilestone = () => ({
  title: "", description: "", category: "autre",
  planned_date: "", actual_date: "", status: "upcoming",
  deliverable: "",
});
const emptyReport = () => ({
  title: "", content: "", milestone_id: "",
  visibility: "private", published_at: "",
});
const emptyTask = () => ({
  title: "", description: "", milestone_id: "",
  due_date: "", status: "todo", assignee_id: "",
});

export default function PlanningTab({ locale }) {
  const isFr = locale === "fr";
  const [milestones, setMilestones] = useState([]);
  const [reports, setReports] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {kind: 'milestone'|'report'|'task', data}
  const [taskView, setTaskView] = useState("kanban"); // 'kanban' | 'grouped'

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, r, t, mb] = await Promise.all([
        api.get("/admin/planning/milestones"),
        api.get("/admin/planning/reports"),
        api.get("/admin/planning/tasks"),
        api.get("/admin/members").catch(() => ({ data: { items: [] } })),
      ]);
      setMilestones(m.data.items || []);
      setReports(r.data.items || []);
      setTasks(t.data.items || []);
      setMembers(mb.data.items || []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const removeMilestone = async (m) => {
    if (!window.confirm(isFr ? `Supprimer le jalon « ${m.title} » ?` : `Delete milestone "${m.title}"?`)) return;
    await api.delete(`/admin/planning/milestones/${m.id}`);
    load();
  };
  const removeReport = async (r) => {
    if (!window.confirm(isFr ? `Supprimer le compte rendu « ${r.title} » ?` : `Delete report "${r.title}"?`)) return;
    await api.delete(`/admin/planning/reports/${r.id}`);
    load();
  };
  const removeTask = async (t) => {
    if (!window.confirm(isFr ? `Supprimer la tâche « ${t.title} » ?` : `Delete task "${t.title}"?`)) return;
    await api.delete(`/admin/planning/tasks/${t.id}`);
    load();
  };
  const updateTaskStatus = async (t, newStatus) => {
    if (t.status === newStatus) return;
    try {
      await api.patch(`/admin/planning/tasks/${t.id}`, { status: newStatus });
      load();
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div data-testid="planning-tab" className="space-y-8">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-3xl">{isFr ? "Planning de projet" : "Project planning"}</h2>
          <p className="font-body text-sm" style={{ color: "var(--muted)" }}>
            {isFr ? "Jalons clés du projet & comptes rendus d'avancement." : "Project milestones & progress reports."}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button className="btn btn-outline" data-testid="planning-new-milestone"
                  onClick={() => setEditing({ kind: "milestone", data: emptyMilestone() })}>
            + {isFr ? "Jalon" : "Milestone"}
          </button>
          <button className="btn btn-outline" data-testid="planning-new-task"
                  onClick={() => setEditing({ kind: "task", data: emptyTask() })}>
            + {isFr ? "Tâche" : "Task"}
          </button>
          <button className="btn btn-primary" data-testid="planning-new-report"
                  onClick={() => setEditing({ kind: "report", data: emptyReport() })}>
            + {isFr ? "Compte rendu" : "Report"}
          </button>
        </div>
      </div>

      {editing?.kind === "milestone" && (
        <MilestoneEditor isFr={isFr} initial={editing.data}
          onCancel={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }} />
      )}
      {editing?.kind === "report" && (
        <ReportEditor isFr={isFr} initial={editing.data} milestones={milestones}
          onCancel={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }} />
      )}
      {editing?.kind === "task" && (
        <TaskEditor isFr={isFr} initial={editing.data} milestones={milestones} members={members}
          onCancel={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }} />
      )}

      {loading && <div className="font-display italic" style={{ color: "var(--muted)" }}>…</div>}

      {/* Milestones timeline */}
      {!loading && (
        <div data-testid="planning-milestones">
          <div className="deco-rule mb-4">
            <span className="font-caps text-[0.78rem]">
              {isFr ? "Jalons" : "Milestones"} <span style={{ color: "var(--muted)" }}>· {milestones.length}</span>
            </span>
          </div>
          {milestones.length === 0 ? (
            <div className="font-body text-sm italic" style={{ color: "var(--muted)" }}>
              {isFr ? "Aucun jalon. Créez-en un pour démarrer le planning." : "No milestone yet."}
            </div>
          ) : (
            <div className="relative pl-8">
              <div className="absolute left-3 top-2 bottom-2 w-px" style={{ background: "var(--border)" }} />
              {milestones.map((m) => {
                const col = STATUS_COLORS[m.status] || STATUS_COLORS.upcoming;
                const linkedReports = reports.filter((r) => r.milestone_id === m.id);
                return (
                  <div key={m.id} data-testid={`milestone-${m.id}`} className="relative mb-6">
                    <div className="absolute -left-6 top-3 w-3 h-3 rounded-full" style={{ background: col.fg }} />
                    <div className="bg-white border p-5" style={{ borderColor: "var(--border)" }}>
                      <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div className="flex-1 min-w-[200px]">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <span className="font-caps text-[0.6rem] px-2 py-0.5 border"
                                  style={{ borderColor: col.fg, color: col.fg, background: col.bg, letterSpacing: "0.18em" }}>
                              {STATUS_LABELS_FR[m.status] || m.status}
                            </span>
                            <span className="font-caps text-[0.55rem]" style={{ color: "var(--muted)", letterSpacing: "0.18em" }}>
                              · {CAT_LABELS_FR[m.category] || m.category}
                            </span>
                          </div>
                          <div className="font-display text-xl">{m.title}</div>
                          {m.description && (
                            <p className="font-body text-sm mt-1" style={{ color: "var(--ink-2)" }}>{m.description}</p>
                          )}
                          <div className="flex flex-wrap gap-3 mt-3 font-caps text-[0.6rem]" style={{ color: "var(--muted)" }}>
                            {m.planned_date && <span>{isFr ? "Prévu" : "Planned"}: {new Date(m.planned_date).toLocaleDateString(locale)}</span>}
                            {m.actual_date && <span>· {isFr ? "Réalisé" : "Done"}: {new Date(m.actual_date).toLocaleDateString(locale)}</span>}
                            {m.deliverable && <span>· {isFr ? "Livrable" : "Deliverable"}: {m.deliverable}</span>}
                          </div>
                        </div>
                        <div className="flex gap-2 shrink-0">
                          <button className="btn btn-outline btn-xs" data-testid={`milestone-edit-${m.id}`}
                                  onClick={() => setEditing({ kind: "milestone", data: { ...m } })}>
                            {isFr ? "Modifier" : "Edit"}
                          </button>
                          <button className="btn btn-outline btn-xs" style={{ color: "var(--cinnabar)" }}
                                  data-testid={`milestone-delete-${m.id}`}
                                  onClick={() => removeMilestone(m)}>
                            {isFr ? "Supprimer" : "Delete"}
                          </button>
                        </div>
                      </div>
                      {linkedReports.length > 0 && (
                        <div className="mt-4 pt-3 border-t" style={{ borderColor: "var(--border)" }}>
                          <div className="font-caps text-[0.55rem] mb-2" style={{ color: "var(--muted)", letterSpacing: "0.18em" }}>
                            {isFr ? "COMPTES RENDUS LIÉS" : "RELATED REPORTS"} · {linkedReports.length}
                          </div>
                          <div className="space-y-1">
                            {linkedReports.map((r) => (
                              <div key={r.id} className="font-body text-xs flex gap-2">
                                <span style={{ color: "var(--muted)" }}>
                                  {new Date(r.published_at).toLocaleDateString(locale)}
                                </span>
                                <span>· {r.title}</span>
                                <VisibilityBadge v={r.visibility} />
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Tasks (Kanban + Grouped views) */}
      {!loading && (
        <div data-testid="planning-tasks">
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <div className="deco-rule">
              <span className="font-caps text-[0.78rem]">
                {isFr ? "Tâches" : "Tasks"} <span style={{ color: "var(--muted)" }}>· {tasks.length}</span>
              </span>
            </div>
            <div className="flex gap-1 border" style={{ borderColor: "var(--border)" }}>
              <button data-testid="task-view-kanban"
                      className="font-caps text-[0.6rem] px-3 py-1.5"
                      style={{
                        background: taskView === "kanban" ? "var(--ink)" : "transparent",
                        color: taskView === "kanban" ? "var(--parchment)" : "var(--ink)",
                        letterSpacing: "0.18em",
                      }}
                      onClick={() => setTaskView("kanban")}>
                {isFr ? "KANBAN" : "KANBAN"}
              </button>
              <button data-testid="task-view-grouped"
                      className="font-caps text-[0.6rem] px-3 py-1.5"
                      style={{
                        background: taskView === "grouped" ? "var(--ink)" : "transparent",
                        color: taskView === "grouped" ? "var(--parchment)" : "var(--ink)",
                        letterSpacing: "0.18em",
                      }}
                      onClick={() => setTaskView("grouped")}>
                {isFr ? "PAR JALON" : "BY MILESTONE"}
              </button>
            </div>
          </div>

          {tasks.length === 0 ? (
            <div className="font-body text-sm italic" style={{ color: "var(--muted)" }}>
              {isFr ? "Aucune tâche. Créez-en une pour démarrer." : "No task yet."}
            </div>
          ) : taskView === "kanban" ? (
            <TaskKanban tasks={tasks} milestones={milestones} members={members} isFr={isFr} locale={locale}
              onEdit={(t) => setEditing({ kind: "task", data: { ...t } })}
              onDelete={removeTask}
              onChangeStatus={updateTaskStatus} />
          ) : (
            <TaskGrouped tasks={tasks} milestones={milestones} members={members} isFr={isFr} locale={locale}
              onEdit={(t) => setEditing({ kind: "task", data: { ...t } })}
              onDelete={removeTask}
              onChangeStatus={updateTaskStatus} />
          )}
        </div>
      )}

      {/* Reports list */}
      {!loading && (
        <div data-testid="planning-reports">
          <div className="deco-rule mb-4">
            <span className="font-caps text-[0.78rem]">
              {isFr ? "Comptes rendus" : "Reports"} <span style={{ color: "var(--muted)" }}>· {reports.length}</span>
            </span>
          </div>
          {reports.length === 0 ? (
            <div className="font-body text-sm italic" style={{ color: "var(--muted)" }}>
              {isFr ? "Aucun compte rendu publié." : "No report yet."}
            </div>
          ) : (
            <div className="space-y-3">
              {reports.map((r) => {
                const m = milestones.find((x) => x.id === r.milestone_id);
                return (
                  <div key={r.id} data-testid={`report-${r.id}`} className="bg-white border p-4" style={{ borderColor: "var(--border)" }}>
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="flex-1 min-w-[200px]">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <VisibilityBadge v={r.visibility} />
                          <span className="font-caps text-[0.55rem]" style={{ color: "var(--muted)", letterSpacing: "0.18em" }}>
                            · {new Date(r.published_at).toLocaleDateString(locale)}
                          </span>
                          {m && (
                            <span className="font-caps text-[0.55rem]" style={{ color: "var(--muted)", letterSpacing: "0.18em" }}>
                              · {isFr ? "JALON" : "MILESTONE"}: {m.title}
                            </span>
                          )}
                        </div>
                        <div className="font-display text-lg">{r.title}</div>
                        <p className="font-body text-sm mt-1 line-clamp-2" style={{ color: "var(--ink-2)" }}>
                          {r.content?.slice(0, 220)}{(r.content?.length || 0) > 220 && "…"}
                        </p>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <button className="btn btn-outline btn-xs" data-testid={`report-edit-${r.id}`}
                                onClick={() => setEditing({ kind: "report", data: { ...r } })}>
                          {isFr ? "Modifier" : "Edit"}
                        </button>
                        <button className="btn btn-outline btn-xs" style={{ color: "var(--cinnabar)" }}
                                data-testid={`report-delete-${r.id}`}
                                onClick={() => removeReport(r)}>
                          {isFr ? "Supprimer" : "Delete"}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function VisibilityBadge({ v }) {
  const c = VIS_COLORS[v] || VIS_COLORS.private;
  return (
    <span className="font-caps text-[0.55rem] px-2 py-0.5 border"
          style={{ borderColor: c.fg, color: c.fg, background: c.bg, letterSpacing: "0.18em" }}>
      {(VIS_LABELS_FR[v] || v).toUpperCase()}
    </span>
  );
}

function MilestoneEditor({ initial, isFr, onCancel, onSaved }) {
  const [m, setM] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k, v) => setM((s) => ({ ...s, [k]: v }));
  const save = async () => {
    setErr("");
    if (!m.title.trim()) { setErr(isFr ? "Titre requis." : "Title required."); return; }
    setBusy(true);
    try {
      const payload = {
        title: m.title, description: m.description, category: m.category,
        planned_date: m.planned_date || null, actual_date: m.actual_date || null,
        status: m.status, owner_id: m.owner_id || null, deliverable: m.deliverable,
      };
      if (m.id) await api.patch(`/admin/planning/milestones/${m.id}`, payload);
      else await api.post("/admin/planning/milestones", payload);
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };
  return (
    <div className="admin-card space-y-4" data-testid="milestone-editor">
      <h3 className="font-display text-xl">{m.id ? (isFr ? "Modifier le jalon" : "Edit milestone") : (isFr ? "Nouveau jalon" : "New milestone")}</h3>
      <label className="block">
        <span className="label-caps">{isFr ? "Titre *" : "Title *"}</span>
        <input className="input-classic mt-1" value={m.title} onChange={(e) => set("title", e.target.value)} data-testid="m-title" />
      </label>
      <label className="block">
        <span className="label-caps">{isFr ? "Description" : "Description"}</span>
        <textarea rows={3} className="input-classic mt-1" value={m.description} onChange={(e) => set("description", e.target.value)} data-testid="m-desc" />
      </label>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="block">
          <span className="label-caps">{isFr ? "Catégorie" : "Category"}</span>
          <select className="input-classic mt-1" value={m.category} onChange={(e) => set("category", e.target.value)} data-testid="m-cat">
            {Object.entries(CAT_LABELS_FR).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="label-caps">{isFr ? "Statut" : "Status"}</span>
          <select className="input-classic mt-1" value={m.status} onChange={(e) => set("status", e.target.value)} data-testid="m-status">
            {Object.entries(STATUS_LABELS_FR).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="label-caps">{isFr ? "Date prévue" : "Planned date"}</span>
          <input type="date" className="input-classic mt-1"
                 value={m.planned_date?.slice(0, 10) || ""}
                 onChange={(e) => set("planned_date", e.target.value)} data-testid="m-planned" />
        </label>
        <label className="block">
          <span className="label-caps">{isFr ? "Date réelle" : "Actual date"}</span>
          <input type="date" className="input-classic mt-1"
                 value={m.actual_date?.slice(0, 10) || ""}
                 onChange={(e) => set("actual_date", e.target.value)} data-testid="m-actual" />
        </label>
      </div>
      <label className="block">
        <span className="label-caps">{isFr ? "Livrable (texte libre)" : "Deliverable (free text)"}</span>
        <input className="input-classic mt-1" value={m.deliverable} onChange={(e) => set("deliverable", e.target.value)}
               placeholder={isFr ? "Ex : dossier de presse v2, plan visiteurs, etc." : "e.g. press kit v2, visitor plan, etc."} />
      </label>
      {err && <p className="text-sm" style={{ color: "var(--cinnabar)" }}>{err}</p>}
      <div className="flex gap-3 justify-end">
        <button className="btn btn-outline" onClick={onCancel} data-testid="m-cancel">{isFr ? "Annuler" : "Cancel"}</button>
        <button className="btn btn-primary" onClick={save} disabled={busy} data-testid="m-save">
          {busy ? "…" : (isFr ? "Enregistrer" : "Save")}
        </button>
      </div>
    </div>
  );
}

function ReportEditor({ initial, milestones, isFr, onCancel, onSaved }) {
  const [r, setR] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k, v) => setR((s) => ({ ...s, [k]: v }));
  const save = async () => {
    setErr("");
    if (!r.title.trim()) { setErr(isFr ? "Titre requis." : "Title required."); return; }
    if (!r.content.trim()) { setErr(isFr ? "Contenu requis." : "Content required."); return; }
    setBusy(true);
    try {
      const payload = {
        title: r.title, content: r.content,
        milestone_id: r.milestone_id || null,
        visibility: r.visibility,
        published_at: r.published_at || new Date().toISOString(),
      };
      if (r.id) await api.patch(`/admin/planning/reports/${r.id}`, payload);
      else await api.post("/admin/planning/reports", payload);
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };
  return (
    <div className="admin-card space-y-4" data-testid="report-editor">
      <h3 className="font-display text-xl">{r.id ? (isFr ? "Modifier le compte rendu" : "Edit report") : (isFr ? "Nouveau compte rendu" : "New report")}</h3>
      <label className="block">
        <span className="label-caps">{isFr ? "Titre *" : "Title *"}</span>
        <input className="input-classic mt-1" value={r.title} onChange={(e) => set("title", e.target.value)} data-testid="r-title" />
      </label>
      <label className="block">
        <span className="label-caps">{isFr ? "Contenu (markdown léger : **gras**, _italique_, listes -, liens [texte](url)) *" : "Content (light markdown) *"}</span>
        <textarea rows={10} className="input-classic mt-1 font-mono text-sm"
                  value={r.content} onChange={(e) => set("content", e.target.value)}
                  placeholder={isFr ? "Décrivez l'avancement, les décisions, les prochaines étapes…" : "Describe progress, decisions, next steps…"}
                  data-testid="r-content" />
      </label>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="block">
          <span className="label-caps">{isFr ? "Lier à un jalon (optionnel)" : "Link to milestone (optional)"}</span>
          <select className="input-classic mt-1" value={r.milestone_id || ""} onChange={(e) => set("milestone_id", e.target.value)} data-testid="r-milestone">
            <option value="">{isFr ? "— Aucun —" : "— None —"}</option>
            {milestones.map((m) => <option key={m.id} value={m.id}>{m.title}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="label-caps">{isFr ? "Visibilité" : "Visibility"}</span>
          <select className="input-classic mt-1" value={r.visibility} onChange={(e) => set("visibility", e.target.value)} data-testid="r-visibility">
            <option value="private">{isFr ? "Privé (admin)" : "Private (admin)"}</option>
            <option value="members">{isFr ? "Membres" : "Members"}</option>
            <option value="public">{isFr ? "Public (site + accueil)" : "Public (site + home)"}</option>
          </select>
        </label>
      </div>
      {err && <p className="text-sm" style={{ color: "var(--cinnabar)" }}>{err}</p>}
      <div className="flex gap-3 justify-end">
        <button className="btn btn-outline" onClick={onCancel} data-testid="r-cancel">{isFr ? "Annuler" : "Cancel"}</button>
        <button className="btn btn-primary" onClick={save} disabled={busy} data-testid="r-save">
          {busy ? "…" : (isFr ? "Enregistrer" : "Save")}
        </button>
      </div>
    </div>
  );
}


// ============================== TASK COMPONENTS ==============================

function TaskCard({ task, milestones, members, isFr, locale, onEdit, onDelete, onChangeStatus }) {
  const col = TASK_STATUS_COLORS[task.status] || TASK_STATUS_COLORS.todo;
  const ms = milestones.find((x) => x.id === task.milestone_id);
  const assignee = members.find((x) => x.id === task.assignee_id);
  const overdue = task.status !== "done" && task.due_date && new Date(task.due_date) < new Date();
  return (
    <div data-testid={`task-${task.id}`}
         className="bg-white border p-3"
         style={{ borderColor: col.border, borderLeftWidth: 4 }}>
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="font-display text-base leading-tight flex-1">{task.title}</div>
        <div className="flex gap-1 shrink-0">
          <button className="text-xs px-1.5 py-0.5 border" data-testid={`task-edit-${task.id}`}
                  style={{ borderColor: "var(--border)", color: "var(--muted)" }}
                  onClick={() => onEdit(task)} title={isFr ? "Modifier" : "Edit"}>
            ✎
          </button>
          <button className="text-xs px-1.5 py-0.5 border" data-testid={`task-delete-${task.id}`}
                  style={{ borderColor: "var(--cinnabar)", color: "var(--cinnabar)" }}
                  onClick={() => onDelete(task)} title={isFr ? "Supprimer" : "Delete"}>
            ✕
          </button>
        </div>
      </div>
      {task.description && (
        <p className="font-body text-xs mt-1" style={{ color: "var(--ink-2)" }}>{task.description}</p>
      )}
      <div className="flex flex-wrap gap-2 mt-2 font-caps text-[0.55rem]" style={{ letterSpacing: "0.12em" }}>
        {ms && (
          <span style={{ color: "var(--gold)" }}>◆ {ms.title}</span>
        )}
        {task.due_date && (
          <span style={{ color: overdue ? "var(--cinnabar)" : "var(--muted)" }}>
            ⏱ {new Date(task.due_date).toLocaleDateString(locale)}
            {overdue && ` · ${isFr ? "RETARD" : "OVERDUE"}`}
          </span>
        )}
        {assignee && (
          <span style={{ color: "var(--lapis)" }}>
            ◯ {assignee.name || assignee.email}
          </span>
        )}
      </div>
      <div className="mt-2 flex gap-1">
        {TASK_STATUSES.map((s) => (
          <button key={s}
                  data-testid={`task-${task.id}-status-${s}`}
                  onClick={() => onChangeStatus(task, s)}
                  className="font-caps text-[0.5rem] px-1.5 py-0.5 border flex-1"
                  style={{
                    letterSpacing: "0.15em",
                    borderColor: TASK_STATUS_COLORS[s].border,
                    background: task.status === s ? TASK_STATUS_COLORS[s].border : "transparent",
                    color: task.status === s ? "#fff" : TASK_STATUS_COLORS[s].fg,
                  }}>
            {TASK_STATUS_LABELS_FR[s].toUpperCase()}
          </button>
        ))}
      </div>
    </div>
  );
}

function TaskKanban({ tasks, milestones, members, isFr, locale, onEdit, onDelete, onChangeStatus }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="task-kanban">
      {TASK_STATUSES.map((s) => {
        const col = TASK_STATUS_COLORS[s];
        const items = tasks.filter((t) => t.status === s);
        return (
          <div key={s} className="bg-white border p-3"
               style={{ borderColor: "var(--border)", borderTopWidth: 3, borderTopColor: col.border }}>
            <div className="flex items-center justify-between mb-3">
              <span className="font-caps text-[0.65rem]" style={{ color: col.fg, letterSpacing: "0.2em" }}>
                {TASK_STATUS_LABELS_FR[s].toUpperCase()}
              </span>
              <span className="font-display text-lg" style={{ color: col.fg }}>{items.length}</span>
            </div>
            <div className="space-y-2 min-h-[100px]">
              {items.map((t) => (
                <TaskCard key={t.id} task={t} milestones={milestones} members={members}
                          isFr={isFr} locale={locale}
                          onEdit={onEdit} onDelete={onDelete} onChangeStatus={onChangeStatus} />
              ))}
              {items.length === 0 && (
                <div className="font-body text-xs italic text-center py-4" style={{ color: "var(--muted)" }}>
                  —
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TaskGrouped({ tasks, milestones, members, isFr, locale, onEdit, onDelete, onChangeStatus }) {
  const groups = milestones.map((m) => ({
    milestone: m,
    items: tasks.filter((t) => t.milestone_id === m.id),
  })).filter((g) => g.items.length > 0);
  const orphan = tasks.filter((t) => !t.milestone_id || !milestones.find((m) => m.id === t.milestone_id));
  if (orphan.length > 0) {
    groups.push({ milestone: null, items: orphan });
  }
  return (
    <div className="space-y-6" data-testid="task-grouped">
      {groups.map((g) => (
        <div key={g.milestone?.id || "orphan"}>
          <div className="font-caps text-[0.7rem] mb-2" style={{ color: "var(--gold)", letterSpacing: "0.2em" }}>
            ◆ {g.milestone ? g.milestone.title : (isFr ? "SANS JALON" : "WITHOUT MILESTONE")}
            <span style={{ color: "var(--muted)" }}> · {g.items.length}</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {g.items.map((t) => (
              <TaskCard key={t.id} task={t} milestones={milestones} members={members}
                        isFr={isFr} locale={locale}
                        onEdit={onEdit} onDelete={onDelete} onChangeStatus={onChangeStatus} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function TaskEditor({ initial, milestones, members, isFr, onCancel, onSaved }) {
  const [t, setT] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k, v) => setT((s) => ({ ...s, [k]: v }));
  const save = async () => {
    setErr("");
    if (!t.title.trim()) { setErr(isFr ? "Titre requis." : "Title required."); return; }
    setBusy(true);
    try {
      const payload = {
        title: t.title, description: t.description,
        milestone_id: t.milestone_id || null,
        due_date: t.due_date || null,
        status: t.status,
        assignee_id: t.assignee_id || null,
      };
      if (t.id) await api.patch(`/admin/planning/tasks/${t.id}`, payload);
      else await api.post("/admin/planning/tasks", payload);
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };
  return (
    <div className="admin-card space-y-4" data-testid="task-editor">
      <h3 className="font-display text-xl">
        {t.id ? (isFr ? "Modifier la tâche" : "Edit task") : (isFr ? "Nouvelle tâche" : "New task")}
      </h3>
      <label className="block">
        <span className="label-caps">{isFr ? "Titre *" : "Title *"}</span>
        <input className="input-classic mt-1" value={t.title} onChange={(e) => set("title", e.target.value)} data-testid="t-title" />
      </label>
      <label className="block">
        <span className="label-caps">{isFr ? "Description" : "Description"}</span>
        <textarea rows={2} className="input-classic mt-1" value={t.description} onChange={(e) => set("description", e.target.value)} data-testid="t-desc" />
      </label>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="block">
          <span className="label-caps">{isFr ? "Jalon (optionnel)" : "Milestone (optional)"}</span>
          <select className="input-classic mt-1" value={t.milestone_id || ""} onChange={(e) => set("milestone_id", e.target.value)} data-testid="t-milestone">
            <option value="">{isFr ? "— Aucun —" : "— None —"}</option>
            {milestones.map((m) => <option key={m.id} value={m.id}>{m.title}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="label-caps">{isFr ? "Statut" : "Status"}</span>
          <select className="input-classic mt-1" value={t.status} onChange={(e) => set("status", e.target.value)} data-testid="t-status">
            {TASK_STATUSES.map((s) => <option key={s} value={s}>{TASK_STATUS_LABELS_FR[s]}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="label-caps">{isFr ? "Échéance" : "Due date"}</span>
          <input type="date" className="input-classic mt-1"
                 value={t.due_date?.slice(0, 10) || ""}
                 onChange={(e) => set("due_date", e.target.value)} data-testid="t-due" />
        </label>
        <label className="block">
          <span className="label-caps">{isFr ? "Assigné à (membre)" : "Assignee (member)"}</span>
          <select className="input-classic mt-1" value={t.assignee_id || ""} onChange={(e) => set("assignee_id", e.target.value)} data-testid="t-assignee">
            <option value="">{isFr ? "— Personne —" : "— Nobody —"}</option>
            {members.map((m) => <option key={m.id} value={m.id}>{m.name || m.email}</option>)}
          </select>
        </label>
      </div>
      {err && <p className="text-sm" style={{ color: "var(--cinnabar)" }}>{err}</p>}
      <div className="flex gap-3 justify-end">
        <button className="btn btn-outline" onClick={onCancel} data-testid="t-cancel">{isFr ? "Annuler" : "Cancel"}</button>
        <button className="btn btn-primary" onClick={save} disabled={busy} data-testid="t-save">
          {busy ? "…" : (isFr ? "Enregistrer" : "Save")}
        </button>
      </div>
    </div>
  );
}
